import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useAuth } from '../context/AuthContext'
import { useVendorAuth } from '../context/VendorAuthContext'
import { fetchUserRedemptions, setSubscribed } from '../state/slices/userSlice'

const SuccessPage = () => {
    const [searchParams] = useSearchParams()
    const { updateUser } = useAuth()
    const { isVendorAuthenticated } = useVendorAuth()
    const dispatch = useDispatch()
    const [status, setStatus] = useState("loading")
    const [message, setMessage] = useState("")
    const [isFeaturedCouponPurchase, setIsFeaturedCouponPurchase] = useState(false)
    const [isCategoryFeaturedCouponPurchase, setIsCategoryFeaturedCouponPurchase] = useState(false)
    const returnPath = searchParams.get("return_path") || ""

    useEffect(() => {
        const sessionId = searchParams.get("session_id")
        const localToken = localStorage.getItem("token")
        const pendingFeaturedCoupon = localStorage.getItem("pendingFeaturedCoupon")
        const pendingCategoryFeaturedCoupon = localStorage.getItem("pendingCategoryFeaturedCoupon")

        if (!sessionId) {
            setStatus("done")
            return
        }

        if (pendingCategoryFeaturedCoupon) {
            setIsCategoryFeaturedCouponPurchase(true)
            localStorage.removeItem("pendingCategoryFeaturedCoupon")

            const activateCategoryFeaturedCoupon = async () => {
                try {
                    const couponData = JSON.parse(pendingCategoryFeaturedCoupon)
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/activate-category-featured`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ session_id: sessionId, ...couponData }),
                    })

                    const data = await response.json()

                    if (response.ok) {
                        setMessage(`Your Category-Featured Coupon "${data.coupon?.title}" is now live in the ${data.coupon?.featured_category} category!`)
                    } else {
                        setMessage(data.error || "Something went wrong creating your category-featured coupon.")
                    }
                } catch (err) {
                    console.error("[SuccessPage] Category-featured coupon activation error:", err)
                    setMessage("Something went wrong confirming your category-featured coupon.")
                } finally {
                    setStatus("done")
                }
            }

            activateCategoryFeaturedCoupon()
            return
        }

        if (pendingFeaturedCoupon) {
            setIsFeaturedCouponPurchase(true)
            // Remove synchronously before the async call so StrictMode's second
            // effect fire sees nothing and skips, preventing a duplicate request.
            localStorage.removeItem("pendingFeaturedCoupon")

            const activateFeaturedCoupon = async () => {
                try {
                    const couponData = JSON.parse(pendingFeaturedCoupon)
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/coupons/activate-featured`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ session_id: sessionId, ...couponData }),
                    })

                    const data = await response.json()

                    if (response.ok) {
                        setMessage(`Your Featured Coupon "${data.coupon?.title}" is now live!`)
                    } else {
                        setMessage(data.error || "Something went wrong creating your featured coupon.")
                    }
                } catch (err) {
                    console.error("[SuccessPage] Featured coupon activation error:", err)
                    setMessage("Something went wrong confirming your featured coupon.")
                } finally {
                    setStatus("done")
                }
            }

            activateFeaturedCoupon()
            return
        }

        if (!localToken) {
            setStatus("done")
            return
        }

        const activateSubscription = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/subscriptions/activate`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ session_id: sessionId }),
                })

                const data = await response.json()

                if (response.status === 201) {
                    updateUser(data.user)
                    dispatch(fetchUserRedemptions({ userId: data.user.id, token: localToken }))
                    dispatch(setSubscribed({
                        isSubscribed: true,
                        subscriptionStartDate: data.subscription?.start_date ?? null,
                    }))
                    setMessage("Your Fun Card Year Subscription is now active!")
                } else if (response.status === 200) {
                    updateUser(data.user)
                    setMessage("Your subscription was already activated.")
                } else {
                    setMessage(data.error || "Something went wrong activating your subscription.")
                }
            } catch (err) {
                console.error("[SuccessPage] Network error:", err)
                setMessage("Something went wrong confirming your subscription.")
            } finally {
                setStatus("done")
            }
        }

        activateSubscription()
    }, [])

    return (
        <section className="py-80">
            <div className="container container-lg">
                <div className="row justify-content-center">
                    <div className="col-lg-6 text-center">
                        <div className="border border-gray-100 rounded-16 p-48">
                            {status === "loading" ? (
                                <p className="text-gray-600">Confirming your purchase...</p>
                            ) : (
                                <>
                                    <div className="mb-24">
                                        <span className="text-success-600 d-flex justify-content-center" style={{ fontSize: 64 }}>
                                            <i className="ph-fill ph-check-circle" />
                                        </span>
                                    </div>
                                    <h4 className="mb-16">Payment Successful!</h4>
                                    {message && (
                                        <p className="text-gray-600 mb-32">{message}</p>
                                    )}
                                    {(isFeaturedCouponPurchase || isCategoryFeaturedCouponPurchase) ? (
                                        <div className="d-flex justify-content-center">
                                            <Link to="/vendor-dashboard" className="btn rounded-8 px-32 py-14" style={{ backgroundColor: '#4F7DC0', borderColor: '#4F7DC0', color: '#fff' }}>
                                                Vendor Dashboard
                                            </Link>
                                        </div>
                                    ) : (
                                        <div className="d-flex justify-content-center gap-16">
                                            <Link
                                                to={isVendorAuthenticated ? "/vendor-dashboard" : "/user-dashboard"}
                                                className="btn rounded-8 px-32 py-14"
                                                style={{ backgroundColor: '#6B9FD4', borderColor: '#6B9FD4', color: '#fff' }}
                                            >
                                                {isVendorAuthenticated ? "Vendor Dashboard" : "User Dashboard"}
                                            </Link>
                                            {returnPath && !isVendorAuthenticated && (
                                                <Link to={returnPath} className="btn rounded-8 px-32 py-14" style={{ backgroundColor: '#4F7DC0', borderColor: '#4F7DC0', color: '#fff' }}>
                                                    Return to Coupon
                                                </Link>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SuccessPage
